// Selection highlights every connected node; arrow directions remain unchanged.
export function traceChain(document, selection) {
  if (!selection) return null
  if (selection.origin === 'motif')
    return { nodeIds: new Set(selection.nodeIds), edgeIds: new Set(selection.edgeIds) }
  const selectedEdge =
    selection.kind === 'relation' ? document.edges.find((e) => e.id === selection.id) : null
  if (selection.kind === 'relation' && !selectedEdge) return null
  const level = selectedEdge?.level || (selection.kind === 'paragraph' ? 'paragraph' : 'unit')
  const nodes = level === 'paragraph' ? document.paragraphs : document.units
  if (!selectedEdge && !nodes.some((n) => n.id === selection.id)) return null
  if (selection.origin === 'narrative' && selection.kind === 'paragraph') {
    const unitIds = new Set(document.units.filter((u) => u.paragraphId === selection.id).map((u) => u.id))
    return {
      nodeIds: new Set([selection.id, ...unitIds]),
      edgeIds: new Set(
        document.edges
          .filter((e) => e.level === 'unit' && unitIds.has(e.source) && unitIds.has(e.target))
          .map((e) => e.id),
      ),
      level,
    }
  }
  const edges = document.edges.filter((e) => e.level === level)
  const nodeIds = new Set(),
    edgeIds = new Set()
  const queue = selectedEdge ? [selectedEdge.source, selectedEdge.target] : [selection.id]
  queue.forEach((id) => nodeIds.add(id))
  for (let i = 0; i < queue.length; i++) {
    for (const edge of edges) {
      if (edge.source !== queue[i] && edge.target !== queue[i]) continue
      edgeIds.add(edge.id)
      const next = edge.source === queue[i] ? edge.target : edge.source
      if (!nodeIds.has(next)) {
        nodeIds.add(next)
        queue.push(next)
      }
    }
  }
  return { nodeIds, edgeIds, level }
}

const overlapArea = (a, b) =>
  Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x)) *
  Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y))

// Compact labels stay beside their node or curve; never search the whole canvas.
const textWidth = (text) => [...text].reduce((width, char) => width + (/[^\x00-\xff]/.test(char) ? 11 : 6), 0)
function wrapLabel(text, maxWidth) {
  const lines = []
  const total = textWidth(text)
  const lineWidth = Math.min(maxWidth, Math.ceil(total / Math.max(1, Math.ceil(total / maxWidth)) / 11) * 11)
  let line = ''
  for (const char of text.replace(/\s+/g, ' ').trim()) {
    if (line && textWidth(line + char) > lineWidth) {
      lines.push(line)
      line = ''
    }
    line += char
  }
  if (line) lines.push(line)
  return lines
}
export function placeLabels(labels, obstacles, viewport) {
  if (viewport.width < 40 || viewport.height < 60) return []
  const placed = []
  for (const label of labels) {
    const { x, y } = label.anchor
    if (!label.text?.trim() || x < 0 || y < 0 || x > viewport.width || y > viewport.height) continue
    const lines = wrapLabel(label.text, Math.min(132, viewport.width - 16))
    const width = Math.max(...lines.map(textWidth)) + 4,
      height = lines.length * 16
    const candidates = []
    const candidate = (left, top, preference = 0) => {
      const box = {
        x: Math.max(4, Math.min(viewport.width - width - 4, left)),
        y: Math.max(4, Math.min(viewport.height - height - 4, top)),
        width,
        height,
      }
      const collision =
        placed.reduce((sum, p) => sum + overlapArea(box, p), 0) * 8 +
        obstacles.reduce((sum, obstacle) => sum + overlapArea(box, obstacle) * (obstacle.weight ?? 1), 0) * 12
      candidates.push({ ...box, score: collision + preference + Math.hypot(box.x - left, box.y - top) * 4 })
    }
    if (label.kind === 'relation') {
      const normal = label.normal || { x: 0, y: -1 }
      const distance = (Math.abs(normal.x) * width) / 2 + (Math.abs(normal.y) * height) / 2 + 6
      for (const sign of [1, -1]) {
        for (const shift of [0, -12, 12]) {
          candidate(
            x + normal.x * distance * sign - normal.y * shift - width / 2,
            y + normal.y * distance * sign + normal.x * shift - height / 2,
            Math.abs(shift) + (sign < 0 ? 2 : 0),
          )
        }
      }
    } else {
      const node = label.bounds || { x, y, width: 0, height: 0 }
      const gap = 6
      candidate(node.x, node.y + node.height + gap)
      candidate(node.x + node.width + gap, node.y + (node.height - height) / 2, 2)
      candidate(node.x, node.y - height - gap, 4)
      candidate(node.x - width - gap, node.y + (node.height - height) / 2, 6)
      candidate(node.x + (node.width - width) / 2, node.y + node.height + gap, 3)
      candidate(node.x + (node.width - width) / 2, node.y - height - gap, 5)
    }
    candidates.sort((a, b) => a.score - b.score)
    placed.push({ ...label, lines, ...candidates[0] })
  }
  return placed
}
